/* eslint-disable react/prop-types */
import { useState, useEffect } from "react";
import API, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Pencil, House, MapPin, IdentificationCard, Key, CurrencyGbp, ArrowSquareOut,
} from "@phosphor-icons/react";

/**
 * Sidebar card showing property-level details for the case:
 *   • Property address  (already on the case at top-level)
 *   • Title number      (HM Land Registry reference, e.g. NK123456)
 *   • Tenure            (freehold / leasehold / commonhold / share of fh)
 *   • Price             (purchase or sale price depending on case type)
 *
 * Visual language matches PartiesCard — same white card, same DetailRow
 * shape, same Pencil-icon inline edit affordance. Click-to-edit opens a
 * single dialog that PUTs to `/api/cases/{id}` (CaseUpdate supports all
 * four fields).
 */
const TENURE_OPTIONS = [
  { value: "freehold", label: "Freehold" },
  { value: "leasehold", label: "Leasehold" },
  { value: "share_of_freehold", label: "Share of freehold" },
  { value: "commonhold", label: "Commonhold" },
];

const TENURE_LABEL = Object.fromEntries(TENURE_OPTIONS.map((o) => [o.value, o.label]));

// Matter (transaction) type — drives which onboarding questionnaire the
// client is sent (Purchase vs Sale vs Remortgage vs Transfer of Equity).
const MATTER_TYPE_OPTIONS = [
  { value: "buying", label: "Purchase" },
  { value: "selling", label: "Sale" },
  { value: "remortgage", label: "Remortgage" },
  { value: "transfer", label: "Transfer of Equity" },
];
const MATTER_TYPE_LABEL = Object.fromEntries(MATTER_TYPE_OPTIONS.map((o) => [o.value, o.label]));

const fmtPrice = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return null;
  // Show 2 decimals only when the figure actually has them — keeps
  // round numbers tidy ("£370,000") while still surfacing the pence on
  // values like £370,000.50 so solicitors don't lose them on screen.
  const hasPence = Math.abs(n - Math.round(n)) > 1e-6;
  return `£${n.toLocaleString("en-GB", {
    minimumFractionDigits: hasPence ? 2 : 0,
    maximumFractionDigits: hasPence ? 2 : 0,
  })}`;
};

export default function PropertyDetailsCard({ caseData, isSolicitor, onUpdated }) {
  const [editing, setEditing] = useState(false);

  if (!caseData) return null;

  const priceLabel = caseData.case_type === "selling" ? "Sale price" : "Purchase price";
  const tenureValue = caseData.tenure ? (TENURE_LABEL[caseData.tenure] || caseData.tenure) : null;

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm" data-testid="property-details-card">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
          <House weight="bold" className="w-4 h-4 text-slate-500" /> Property details
        </h4>
        {isSolicitor && (
          <Button
            onClick={() => setEditing(true)}
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs gap-1"
            data-testid="property-details-edit-btn"
          >
            <Pencil weight="bold" className="w-3.5 h-3.5" /> Edit
          </Button>
        )}
      </div>

      <dl className="space-y-1.5 text-[11px]" data-testid="property-details-list">
        <DetailRow
          icon={<House weight="bold" className="w-3 h-3 text-slate-400" />}
          label="Matter"
          value={MATTER_TYPE_LABEL[caseData.case_type] || caseData.case_type || "—"}
          testid="property-details-matter-type"
        />
        <DetailRow
          icon={<MapPin weight="bold" className="w-3 h-3 text-slate-400" />}
          label="Address"
          value={caseData.property_address || "—"}
          testid="property-details-address"
        />
        <DetailRow
          icon={<IdentificationCard weight="bold" className="w-3 h-3 text-slate-400" />}
          label="Title №"
          value={caseData.title_number || "—"}
          testid="property-details-title-number"
          action={(
            <button
              type="button"
              onClick={async () => {
                if (caseData.title_number) {
                  try {
                    if (navigator.clipboard?.writeText) {
                      await navigator.clipboard.writeText(caseData.title_number);
                      toast.success(`Copied "${caseData.title_number}" — paste it into the search.`);
                    }
                  } catch {
                    // Clipboard is best-effort — we still open the search page.
                  }
                } else {
                  toast.info("Search by address on Land Registry, then paste the title number into the property details.");
                }
                window.open("https://www.gov.uk/search-property-information-land-registry", "_blank", "noopener,noreferrer");
              }}
              title={caseData.title_number
                ? "Copy title number and open HM Land Registry search"
                : "Open HM Land Registry search by address"}
              data-testid="property-details-title-lookup-btn"
              className="ml-1 inline-flex items-center gap-0.5 text-[10px] font-medium text-blue-600 hover:underline shrink-0"
            >
              Lookup <ArrowSquareOut weight="bold" className="w-2.5 h-2.5" />
            </button>
          )}
        />
        <DetailRow
          icon={<Key weight="bold" className="w-3 h-3 text-slate-400" />}
          label="Tenure"
          value={tenureValue || "—"}
          testid="property-details-tenure"
        />
        <DetailRow
          icon={<CurrencyGbp weight="bold" className="w-3 h-3 text-slate-400" />}
          label={priceLabel}
          value={fmtPrice(caseData.property_value) || "—"}
          testid="property-details-price"
        />
      </dl>

      <PropertyDetailsDialog
        open={editing}
        caseData={caseData}
        onClose={() => setEditing(false)}
        onSaved={(updated) => {
          setEditing(false);
          onUpdated?.(updated);
        }}
      />
    </div>
  );
}

function DetailRow({ icon, label, value, testid, action }) {
  return (
    <div className="flex items-start gap-2" data-testid={testid}>
      <span className="shrink-0 mt-0.5">{icon}</span>
      <span className="text-slate-500 w-16 shrink-0">{label}</span>
      <span className="text-slate-900 break-words min-w-0 flex-1">{value}</span>
      {action}
    </div>
  );
}

function PropertyDetailsDialog({ open, caseData, onClose, onSaved }) {
  const [form, setForm] = useState({
    property_address: "",
    title_number: "",
    tenure: "",
    property_value: "",
    case_type: "buying",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      property_address: caseData?.property_address || "",
      title_number: caseData?.title_number || "",
      tenure: caseData?.tenure || "",
      property_value: caseData?.property_value != null ? String(caseData.property_value) : "",
      case_type: caseData?.case_type || "buying",
    });
  }, [open, caseData]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      // Send every field explicitly so empty strings clear server-side
      // (CaseUpdate normalises "" → null for title_number/tenure and
      // accepts "" for property_value).
      const payload = {
        property_address: form.property_address.trim(),
        title_number: form.title_number.trim(),
        tenure: form.tenure || "",
        property_value: form.property_value === "" ? null : Number(form.property_value),
        case_type: form.case_type || "buying",
      };
      const { data } = await API.put(`/cases/${caseData.id}`, payload);
      toast.success("Property details saved");
      onSaved?.(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="property-details-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit, sans-serif" }}>Edit property details</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Matter type</Label>
            <Select value={form.case_type || "buying"} onValueChange={(v) => setForm((f) => ({ ...f, case_type: v }))}>
              <SelectTrigger data-testid="property-details-matter-type-select" className="h-9 text-xs">
                <SelectValue placeholder="Select…" />
              </SelectTrigger>
              <SelectContent>
                {MATTER_TYPE_OPTIONS.map((m) => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-slate-500">Determines which onboarding questionnaire the client is sent.</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Property address</Label>
            <Input
              value={form.property_address}
              onChange={set("property_address")}
              placeholder="1 Example Street, Norwich, NR1 1AA"
              data-testid="property-details-address-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Title number</Label>
              <Input
                value={form.title_number}
                onChange={set("title_number")}
                placeholder="e.g., NK123456"
                data-testid="property-details-title-number-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Tenure</Label>
              <Select value={form.tenure || "_none"} onValueChange={(v) => setForm((f) => ({ ...f, tenure: v === "_none" ? "" : v }))}>
                <SelectTrigger data-testid="property-details-tenure-select" className="h-9 text-xs">
                  <SelectValue placeholder="Select…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">— Not set —</SelectItem>
                  {TENURE_OPTIONS.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">
              {caseData?.case_type === "selling" ? "Sale price" : "Purchase price"}
            </Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm pointer-events-none">£</span>
              <Input
                value={form.property_value}
                onChange={set("property_value")}
                type="number"
                inputMode="decimal"
                min="0"
                step="1000"
                placeholder="e.g., 325000"
                data-testid="property-details-price-input"
                className="pl-7"
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="text-white"
            style={{ background: "#100f5d" }}
            data-testid="property-details-save-btn"
          >
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
